import * as React from "react";
import { 
    List,
    Datagrid, Filter, TopToolbar, SortButton, ExportButton,
    TextField, FunctionField,
    ReferenceInput, SelectInput, RadioButtonGroupInput,
    useListSortContext
} from 'react-admin';
import { TableHead, TableRow, TableCell, useMediaQuery, makeStyles } from '@material-ui/core';
import Config from "../../providers/configProvider";
import LogoField from '../../components/LogoField';

const PlayerFilter = props => {
    const isSmall = useMediaQuery(theme => theme.breakpoints.down('sm'));
    return (
        <Filter {...props} style={{marginTop: isSmall ? '0px' : '-16px'}}>
            <ReferenceInput label="Team" source="teamId" reference="team" perPage={1000} alwaysOn>
                <SelectInput optionText={<LogoField type="teams" source="code" text="name" />} disabled={!props.enableTeam} />
            </ReferenceInput>
            <ReferenceInput label="Player" source="playerId" reference="player" perPage={1000} allowEmpty={false} alwaysOn>
                <SelectInput optionText={record => `${record.lastName}, ${record.firstName}`} />
            </ReferenceInput>
            <RadioButtonGroupInput label="Display" source="showAverages" choices={[
                { id: false, name: 'Totals' },
                { id: true, name: 'Averages' }
            ]} alwaysOn />
        </Filter>
    );
};

const ListActions = ({ sortFields }) => (
    <TopToolbar>
        <SortButton fields={sortFields} />
        <ExportButton />
    </TopToolbar>
);

const useStyles = makeStyles({
    cellSmall: {
        padding: '8px'
    },
    cell: {
        textAlign: 'right'
    },
    cellLeft: {
        textAlign: 'left'
    },
    cellBorder: {
        borderLeft: '1px solid #000',
        textAlign: 'right'
    },
    cellBorderLeft: {
        borderLeft: '1px solid #000',
        textAlign: 'left'
    },
    cellBorderDouble: {
        borderLeft: '3px double #000',
        textAlign: 'right'
    },
    topHeader: {
        fontWeight: 'bold',
        textAlign: 'center'
    },
    topHeaderBorder: {
        fontWeight: 'bold',
        textAlign: 'center',
        borderLeft: '1px solid #000'
    },
    topHeaderBorderDouble: {
        fontWeight: 'bold',
        textAlign: 'center',
        borderLeft: '3px double #000'
    },
    green: {
        fontWeight: 'bold',
        color: 'green'
    },
    greenSmall: {
        color: 'green'
    },
    red: {
        fontWeight: 'bold',
        color: 'red'
    },
    redSmall: {
        color: 'red'
    }
});

/*** PLAYER STATS HISTORY ***/
const playerStatsSortFields = [
    { field: 'season', label: 'season' },
    { field: 'primaryPosition.name', label: 'primary position' },  
    { field: 'teamRecord.played', label: 'played' }, 
    { field: 'teamRecord.won', label: 'won' },
    { field: 'teamRecord.drawn', label: 'drawn' },
    { field: 'teamRecord.lost', label: 'lost' },
    { field: 'teamRecord.winPercentage', label: 'win percentage' },
    { field: 'tries', label: 'tries' },
    { field: 'goals', label: 'goals' },
    { field: 'fieldGoals1', label: '1 point field goals' },
    { field: 'fieldGoals2', label: '2 point field goals' },
    { field: 'points', label: 'points' },
    { field: 'sinBins', label: 'sin bins' },
    { field: 'sendOffs', label: 'send offs' }
];

const PlayerStatHeader = ({ children }) => {
    const classes = useStyles();
    const { currentSort } = useListSortContext();
    return (
        <TableHead>
            <TableRow>
                <TableCell colSpan={3} />
                <TableCell colSpan={8} className={classes.topHeader}>Results</TableCell>
                <TableCell colSpan={7} className={classes.topHeaderBorder}>Stats</TableCell>
            </TableRow>
            <TableRow>
                {React.Children.map(children, child => (
                    <TableCell key={child.props.source} className={child.props.source && child.props.source === 'tries' ? classes.cellBorder : (child.props.label === 'Competition' || child.props.label === 'Season' || child.props.label === 'Primary Position' ? classes.cellLeft : classes.cell)}>
                        {child.props.label}{playerStatsSortFields.find(f => f.field === child.props.source) && currentSort.field === playerStatsSortFields.find(f => f.field === child.props.source).label
                            ? (currentSort.order === 'DESC' ? <span> &darr;</span> : <span> &uarr;</span>)
                            :  null}
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
};

export const PlayerStatHistoryList = props => {
    const isSmall = useMediaQuery(theme => theme.breakpoints.down('sm'));
    const classes = useStyles();
    return (
        <List {...props} 
            bulkActionButtons={false}
            filters={<PlayerFilter enableTeam={false} />}
            filterDefaultValues={{ teamId: Config.MyTeamId, showAverages: false }}
            actions={<ListActions sortFields={playerStatsSortFields.map(f => f.label)} />}
            sort={{ field: 'season', order: 'DESC' }}
            pagination={null}
        >
            {isSmall ? (
                <Datagrid
                    classes={{ rowCell: classes.cellSmall, headerCell: classes.cellSmall }}
                    rowClick={(id, basePath, record) => `/stat/players/${record.id};${record.playerId}/show`}
                >
                    <LogoField type="competitions" source="competitionCode" text="competitionCode" label="Competition" />
                    <TextField source="seasonYear" label="Season" /> 
                    <TextField source="teamRecord.played" label="P" />
                    <TextField source="teamRecord.winPercentage" label="W%" />   
                    <TextField source="tries" label="T" emptyText="-" />
                    <TextField source="goals" label="G" emptyText="-" />
                    <FunctionField render={record => record.fieldGoals1 !== null ? record.fieldGoals1 + record.fieldGoals2 : '-'} label="FG" />
                    <TextField source="points" label="Pts" emptyText="-" />
                </Datagrid>
            ) : (
                <Datagrid 
                    header={<PlayerStatHeader />}
                    classes={{ rowCell: classes.cell, headerCell: classes.cell }}
                    rowClick={(id, basePath, record) => `/stat/players/${record.id};${record.playerId}/show`}
                >
                    <LogoField type="competitions" source="competitionCode" text="competitionCode" label="Competition" cellClassName={classes.cellLeft} />
                    <TextField source="seasonYear" label="Season" cellClassName={classes.cellLeft} />
                    <TextField source="primaryPosition.name" label="Primary Position" cellClassName={classes.cellLeft} />
                    <TextField source="teamRecord.played" label="P"/>
                    <TextField source="teamRecord.won" label="W" />
                    <TextField source="teamRecord.drawn" label="D" />
                    <TextField source="teamRecord.lost" label="L" />
                    <TextField source="teamRecord.winPercentage" label="W%" />
                    <TextField source="teamRecord.pointsFor" label="F" />
                    <TextField source="teamRecord.pointsAgainst" label="A" />
                    <TextField source="teamRecord.pointsDiff" label="+/-" />  
                    <TextField source="tries" label="T" emptyText="-" cellClassName={classes.cellBorder}  />
                    <TextField source="goals" label="G" emptyText="-" />
                    <TextField source="fieldGoals1" label="FG1" emptyText="-" />
                    <TextField source="fieldGoals2" label="FG2" emptyText="-" />
                    <TextField source="points" label="Pts" emptyText="-"/>
                    <TextField source="sinBins" label="SB" emptyText="-" />
                    <TextField source="sendOffs" label="SO" emptyText="-" />
                </Datagrid>
            )}
        </List>
    );
};